import { API_BASE_URL } from '../config/api';
import { getAuthHeaders } from './utils/authTokenUtils';
import { ApiError } from './utils/apiError';

export const FEATURE_FLAGS_STORAGE_KEY = 'pixleProFeatureFlags';

/** When enabled, hides the configured tiles on the toolbar/nav bar. */
export const FEATURE_FLAG_HIDDEN_TILES = 'hidden_tiles';

/** When enabled, reveals the skeletal rigging tools in the Character Studio
 * (rig editor, actions, animation recording, add-to-canvas). Character
 * creation, identity, and voice are always available for video generation. */
export const FEATURE_FLAG_CHARACTER_RIGGING = 'characterRiggingEnabled';

export interface FeatureFlags {
  insights_v2?: boolean;
  runway_gen4_turbo?: boolean;
  hidden_tiles?: boolean;
  characterRiggingEnabled?: boolean;
  delete_video_enabled?: boolean;
  [key: string]: boolean | undefined;
}

interface FeatureFlagsResponse {
  featureFlags?: FeatureFlags;
}

export const getFeatureFlags = async (): Promise<FeatureFlags> => {
  const response = await fetch(`${API_BASE_URL}/auth/feature-flags`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new ApiError('Failed to fetch feature flags', response.status);
  }

  const data: FeatureFlagsResponse = await response.json();
  return data.featureFlags || {};
};

export const cacheFeatureFlags = (featureFlags: FeatureFlags): void => {
  try {
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(featureFlags));
  } catch (error) {
    console.error('Failed to cache feature flags', error);
  }
};

export const isRunwayGen4TurboEnabled = (): boolean => {
  try {
    const rawFlags = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!rawFlags) return false;
    const featureFlags = JSON.parse(rawFlags) as FeatureFlags;
    return featureFlags.runway_gen4_turbo === true;
  } catch {
    return false;
  }
};

export const isHiddenTilesEnabled = (): boolean => {
  try {
    const rawFlags = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!rawFlags) return false;
    const featureFlags = JSON.parse(rawFlags) as FeatureFlags;
    return featureFlags[FEATURE_FLAG_HIDDEN_TILES] === true;
  } catch {
    return false;
  }
};

export const isCharacterRiggingEnabled = (): boolean => {
  try {
    const rawFlags = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!rawFlags) return false;
    const featureFlags = JSON.parse(rawFlags) as FeatureFlags;
    return featureFlags[FEATURE_FLAG_CHARACTER_RIGGING] === true;
  } catch {
    return false;
  }
};
